import React, { useState, useEffect } from 'react';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Download, FileSpreadsheet, RefreshCw, AlertCircle } from 'lucide-react';
import reportService from '../../services/reportService';
import cashBoxService, { CashBox } from '../../services/cashBoxService';
import InlineDatePicker from '../InlineDatePicker';
import { showError, showSuccess } from '../Alerts';
import { exportMovementsToExcel } from '../../utils/exportExcel';

interface CashBoxExportModalProps {
  isOpen: boolean;
  onClose: () => void;
  cashBoxId: number | null;
}

export default function CashBoxExportModal({ isOpen, onClose, cashBoxId }: CashBoxExportModalProps) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [cashBoxes, setCashBoxes] = useState<CashBox[]>([]);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');

  useEffect(() => {
    if (!isOpen || cashBoxId === null) return;
    const load = async () => {
      try {
        const boxes = await cashBoxService.getAllCashBoxes();
        setCashBoxes(boxes);
        const today = new Date();
        const firstDay = new Date(today.getFullYear(), today.getMonth(), 1);
        const lastDay = new Date(today.getFullYear(), today.getMonth() + 1, 0);
        setStartDate(firstDay.toISOString().split('T')[0]);
        setEndDate(lastDay.toISOString().split('T')[0]);
        setError(null);
      } catch {
        showError('Error al cargar la información de la caja');
      }
    };
    load();
  }, [isOpen, cashBoxId]);

  useEffect(() => {
    if (!isOpen) setError(null);
  }, [isOpen]);

  const selectedBox = cashBoxes.find((b) => b.id === cashBoxId);
  const currency = selectedBox?.currency || 'ARS';

  const handleExport = async () => {
    if (!cashBoxId || !startDate || !endDate) {
      showError('Seleccioná un rango de fechas válido');
      return;
    }
    if (startDate > endDate) {
      showError('La fecha de inicio no puede ser posterior a la fecha de fin');
      return;
    }
    setError(null);
    setLoading(true);
    try {
      const data = await reportService.getCashBoxMovements({ cashBoxId, startDate, endDate });
      if (data.length === 0) {
        showError('No hay movimientos en el período seleccionado para exportar');
        return;
      }
      exportMovementsToExcel(data, selectedBox?.name || 'Caja', currency, startDate, endDate);
      showSuccess(`Exportación completada: ${data.length} movimiento${data.length !== 1 ? 's' : ''}`);
      onClose();
    } catch (err: any) {
      const msg = err.response?.data?.message || 'Error al exportar los movimientos.';
      setError(msg);
      showError(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => { if (!open) onClose(); }}>
      <DialogContent className="w-[95vw] max-w-md bg-card border-border p-0 flex flex-col">

        {/* Header */}
        <DialogHeader className="shrink-0 px-5 pt-5 pb-4 border-b border-border">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-success/10 flex items-center justify-center shrink-0">
              <FileSpreadsheet className="w-5 h-5 text-success" />
            </div>
            <div>
              <DialogTitle className="text-base font-bold text-foreground">
                Exportar Movimientos a Excel
              </DialogTitle>
              <p className="text-sm text-muted-foreground flex items-center gap-1.5 mt-0.5">
                {selectedBox?.name || 'Cargando...'}
                {currency && (
                  <Badge variant="outline" className="px-1.5 py-0 h-5 text-[10px]">{currency}</Badge>
                )}
              </p>
            </div>
          </div>
        </DialogHeader>

        {/* Body */}
        <div className="px-5 py-5 space-y-4">
          <p className="text-sm text-muted-foreground">
            Seleccioná el rango de fechas para incluir en el archivo Excel.
          </p>

          <div className="grid grid-cols-2 gap-3">
            <InlineDatePicker
              id="export-desde"
              label="Desde"
              value={startDate}
              onChange={setStartDate}
            />
            <InlineDatePicker
              id="export-hasta"
              label="Hasta"
              value={endDate}
              onChange={setEndDate}
            />
          </div>

          {error && (
            <div className="flex items-center gap-3 p-3 text-sm rounded-xl bg-destructive/5 border border-destructive/20 text-destructive">
              <AlertCircle className="w-4 h-4 shrink-0" />
              <span>{error}</span>
            </div>
          )}

          <div className="flex items-start gap-2.5 px-3 py-3 rounded-lg bg-muted/40 border border-border text-xs text-muted-foreground">
            <FileSpreadsheet className="w-4 h-4 shrink-0 mt-0.5 text-success" />
            <span>
              El archivo incluirá: fecha, hora, descripción, referencia, tipo de movimiento, monto.
            </span>
          </div>
        </div>

        {/* Footer */}
        <div className="shrink-0 flex flex-col-reverse gap-2 px-5 py-4 border-t border-border sm:flex-row sm:justify-end">
          <Button variant="outline" onClick={onClose} disabled={loading} className="w-full rounded-lg sm:w-auto">
            Cancelar
          </Button>
          <Button
            onClick={handleExport}
            disabled={loading || !startDate || !endDate}
            className="w-full rounded-lg bg-success text-success-foreground hover:opacity-90 sm:w-auto"
          >
            {loading ? (
              <>
                <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                Exportando...
              </>
            ) : (
              <>
                <Download className="w-4 h-4 mr-2" />
                Descargar Excel
              </>
            )}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
